import React, { useEffect, useState } from "react";
import moment from "moment";
import { CircularProgress } from "@mui/material";
import StarRating from "./StarRating";
import { getMyFeedback } from "../../api/feedback";

/**
 * "Submitted surveys" — every survey the logged-in patient has answered, from
 * whichever door (portal, emailed link, clinic QR), with the per-question
 * ratings and comment on expand. Reads GET /patient-feedback/me/responses.
 * `refreshKey` re-fetches after a new submission.
 */
const SubmittedSurveys = ({ refreshKey = 0 }) => {
  const [responses, setResponses] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let alive = true;
    setError("");
    getMyFeedback()
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data?.responses) ? data.responses : [];
        setResponses(
          [...list].sort(
            (a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0)
          )
        );
      })
      .catch((err) => {
        if (alive) {
          setError(err?.message || "Could not load your submitted surveys");
          setResponses([]);
        }
      });
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  return (
    <>
      <h3 className="fb_section">Submitted surveys</h3>
      {responses === null ? (
        <div className="fb_center">
          <CircularProgress size="1.2rem" />
        </div>
      ) : error ? (
        <p className="fb_error" role="alert">
          {error}
        </p>
      ) : responses.length === 0 ? (
        <p className="fb_muted fb_center">
          Surveys you submit will appear here.
        </p>
      ) : (
        <ul className="fb_list" aria-label="Submitted surveys">
          {responses.map((r) => {
            const open = openId === r.responseId;
            const ratings = (r.answers || []).filter(
              (a) => a.type !== "text" && a.rating != null
            );
            const texts = (r.answers || []).filter(
              (a) => a.type === "text" && a.answer
            );
            return (
              <li key={r.responseId} className="fb_card fb_row fb_row_stack">
                <button
                  type="button"
                  className="fb_row_button"
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : r.responseId)}
                >
                  <div className="fb_row_main">
                    <div className="fb_row_title">{r.clinicName || "Clinic"}</div>
                    <div className="fb_muted">
                      {[r.doctorName, r.surveyName].filter(Boolean).join(" · ")}
                    </div>
                    <div className="fb_muted fb_small">
                      {r.submittedAt
                        ? `Submitted ${moment(r.submittedAt).format("DD MMM YYYY, h:mm A")}`
                        : ""}
                    </div>
                  </div>
                  <span className="fb_score" aria-label="Overall rating">
                    ★ {typeof r.overall === "number" ? r.overall.toFixed(1) : "—"}
                    <span className="fb_small"> / 5</span>
                  </span>
                </button>
                {open ? (
                  <div className="fb_row_detail">
                    <ul className="fb_analysis_list" aria-label="Your ratings">
                      {ratings.map((a) => (
                        <li key={a.key} className="fb_analysis_row">
                          <span className="fb_analysis_text">{a.text}</span>
                          <span className="fb_analysis_rating">
                            <StarRating value={a.rating} size={16} />
                            <span>{a.rating}/5</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                    {texts.map((a) => (
                      <p key={a.key} className="fb_muted fb_small">
                        <strong>{a.text}</strong> {a.answer}
                      </p>
                    ))}
                    {r.comment ? (
                      <p className="fb_muted fb_small">
                        <strong>Your comment:</strong> {r.comment}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

export default SubmittedSurveys;
